import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Pagination from '../components/Pagination';
import './Dashboard.css';

const PuntosVenta = () => {
  const { user } = useContext(AuthContext);
  const [puntos, setPuntos] = useState([]);
  const [concesionarios, setConcesionarios] = useState([]);
  const [zonasDisponibles, setZonasDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingPunto, setEditingPunto] = useState(null);
  const [formData, setFormData] = useState({
    concesionario_id: '',
    zonas_ids: [],
    name: '',
    address: '',
    city: '',
    phone: ''
  });

  // Paginación y Filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterConcesionario, setFilterConcesionario] = useState('');

  const fetchPuntosVenta = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/puntos-venta');
      setPuntos(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar puntos de venta');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConcesionarios = useCallback(async () => {
    try {
      if (user?.role === 'superadmin') {
        const res = await axios.get('/api/concesionarios');
        setConcesionarios(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [user?.role]);

  const fetchZonasGlobales = useCallback(async () => {
    try {
      const res = await axios.get('/api/zonas');
      setZonasDisponibles(res.data);
    } catch (err) {
      console.error('Error al cargar zonas globales:', err);
      setZonasDisponibles([]);
    }
  }, []);

  useEffect(() => {
    fetchPuntosVenta();
    fetchConcesionarios();
    fetchZonasGlobales();
  }, [fetchPuntosVenta, fetchConcesionarios, fetchZonasGlobales]);

  const handleConcesionarioChange = (concId) => {
    setFormData(prev => ({ ...prev, concesionario_id: concId }));
  };

  const handleOpenCreateModal = () => {
    const defaultConcId = user?.concesionario_id || '';
    setEditingPunto(null);
    setFormData({
      concesionario_id: defaultConcId,
      zonas_ids: [],
      name: '',
      address: '',
      city: '',
      phone: ''
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (punto) => {
    setEditingPunto(punto);
    setFormData({
      concesionario_id: punto.concesionario_id,
      zonas_ids: punto.zonas ? punto.zonas.map(z => z.id) : [],
      name: punto.name,
      address: punto.address || '',
      city: punto.city || '',
      phone: punto.phone || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      if (editingPunto) {
        await axios.put(`/api/puntos-venta/${editingPunto.id}`, formData);
        setSuccess('Punto de venta actualizado correctamente');
      } else {
        await axios.post('/api/puntos-venta', formData);
        setSuccess('Punto de venta creado correctamente');
      }
      setShowModal(false);
      fetchPuntosVenta();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar punto de venta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este punto de venta?')) return;
    try {
      setError('');
      setSuccess('');
      await axios.delete(`/api/puntos-venta/${id}`);
      setSuccess('Punto de venta eliminado con éxito');
      fetchPuntosVenta();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar punto de venta');
    }
  };

  // Filtrado y Paginación
  const filteredPuntos = puntos.filter(p => {
    const matchesSearch = 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.concesionario_name && p.concesionario_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.zonas && p.zonas.some(z => z.name.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (p.city && p.city.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const matchesConcesionario = filterConcesionario === '' || p.concesionario_id === parseInt(filterConcesionario);
    
    return matchesSearch && matchesConcesionario;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPuntos.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🏪 Puntos de Venta" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner" style={{ padding: '12px', marginBottom: '20px', background: '#ecfdf5', color: '#065f46', borderRadius: '8px', border: '1px solid #a7f3d0' }}>{success}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Gestión de Puntos de Venta</h2>
              <p className="subtitle">Administra las sucursales y puntos de atención de concesionarios</p>
            </div>
            {user?.profile_name !== 'administrativo_concesionario' && (
            <button onClick={handleOpenCreateModal} className="btn-primary">
              ➕ Crear Punto de Venta
            </button>
            )}
          </div>

          <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', gap: '15px' }}>
            <div className="search-box" style={{ flex: 1 }}>
              <input 
                type="text" 
                placeholder="🔍 Buscar por punto de venta, concesionario, zona o ciudad..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="form-input"
                style={{ width: '100%', maxWidth: '340px' }}
              />
            </div>
            
            {user?.role === 'superadmin' && concesionarios.length > 0 && (
              <div className="filter-box">
                <select 
                  className="form-input" 
                  value={filterConcesionario} 
                  onChange={(e) => { setFilterConcesionario(e.target.value); setCurrentPage(1); }}
                  style={{ minWidth: '200px' }}
                >
                  <option value="">Todos los Concesionarios</option>
                  {concesionarios.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {loading ? (
            <p className="loading">Cargando puntos de venta...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre del Punto de Venta</th>
                      <th>Concesionario</th>
                      <th>Zonas</th>
                      <th>Ciudad</th>
                      <th>Dirección</th>
                      <th>Teléfono</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map(p => (
                      <tr key={p.id}>
                        <td><strong>#{p.id}</strong></td>
                        <td><strong style={{ color: '#1e293b' }}>{p.name}</strong></td>
                        <td>
                          <span style={{ fontWeight: '600', color: '#2563eb' }}>{p.concesionario_name}</span>
                        </td>
                        <td>
                          {p.zonas && p.zonas.length > 0 ? (
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              {p.zonas.map(z => (
                                <span key={z.id} style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                                  📍 {z.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>- Sin Zona -</span>
                          )}
                        </td>
                        <td>{p.city || 'N/A'}</td>
                        <td>{p.address || 'N/A'}</td>
                        <td>{p.phone || 'N/A'}</td>
                        <td className="actions">
                          {user?.profile_name !== 'administrativo_concesionario' && (<>
                          <button onClick={() => handleOpenEditModal(p)} className="btn-action btn-edit" title="Editar">
                            ✏️
                          </button>
                          <button onClick={() => handleDelete(p.id)} className="btn-action btn-delete" title="Eliminar">
                            🗑️
                          </button>
                          </>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredPuntos.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron puntos de venta registrados.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredPuntos.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(items) => { setItemsPerPage(items); setCurrentPage(1); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* MODAL CREAR / EDITAR */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3>{editingPunto ? '✏️ Editar Punto de Venta' : '➕ Crear Punto de Venta'}</h3>
            <form onSubmit={handleSubmit}>
              {user?.role === 'superadmin' && (
                <div className="form-group">
                  <label>Concesionario *</label>
                  <select 
                    value={formData.concesionario_id} 
                    onChange={(e) => handleConcesionarioChange(e.target.value)}
                    required
                    className="form-input"
                  >
                    <option value="">Seleccione Concesionario...</option>
                    {concesionarios.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Zonas (Obligatorio seleccionar al menos una)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '5px' }}>
                  {zonasDisponibles.map(z => (
                    <label key={z.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#f8fafc', padding: '5px 10px', borderRadius: '5px', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.zonas_ids.includes(z.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setFormData(prev => ({ ...prev, zonas_ids: [...prev.zonas_ids, z.id] }));
                          } else {
                            setFormData(prev => ({ ...prev, zonas_ids: prev.zonas_ids.filter(id => id !== z.id) }));
                          }
                        }}
                      />
                      <span>{z.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Nombre del Punto de Venta *</label>
                <input 
                  type="text"
                  placeholder="Ej: Las Delicias, Cordialidad, etc."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Ciudad</label>
                <input 
                  type="text"
                  placeholder="Ej: Barranquilla, Soledad..."
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input 
                  type="text"
                  placeholder="Ej: Calle 45 # 10-20"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Teléfono de Contacto</label>
                <input 
                  type="text"
                  placeholder="Ej: 3001234567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingPunto ? 'Guardar Cambios' : 'Crear Punto de Venta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PuntosVenta;
